import { LinkedAssetFrame } from 'types/detailsEntityType';
import { TLinkedAssetFramesTableEntity } from 'types/tableEntityType';
import { LinkedAssetFrameStatus } from 'utils/statuses';

export const linkedAssetFrameWithoutDiff = (
  linkedAssetFrame: LinkedAssetFrame,
  status: LinkedAssetFrameStatus | null
) => ({
  spaceId: linkedAssetFrame.spaceId,
  name: { current: linkedAssetFrame.name },
  broadsignFrames: { current: linkedAssetFrame.broadsignFrames },
  status: status,
});

const getUpdatedLinkedAssetFrame = (
  currentLinkedAssetFrame: LinkedAssetFrame,
  previousLinkedAssetFrame: LinkedAssetFrame
): TLinkedAssetFramesTableEntity | null => {
  const nameChanged = previousLinkedAssetFrame.name !== currentLinkedAssetFrame.name;
  const broadsignFramesChanged =
    JSON.stringify(previousLinkedAssetFrame.broadsignFrames) !==
    JSON.stringify(currentLinkedAssetFrame.broadsignFrames);

  if (!nameChanged && !broadsignFramesChanged) return null;

  return {
    spaceId: currentLinkedAssetFrame.spaceId,
    name: {
      current: currentLinkedAssetFrame.name,
      previous: nameChanged ? previousLinkedAssetFrame.name : null,
    },
    broadsignFrames: {
      current: currentLinkedAssetFrame.broadsignFrames,
      previous: broadsignFramesChanged ? previousLinkedAssetFrame.broadsignFrames : null,
    },
    status: LinkedAssetFrameStatus.UPDATED,
  };
};

const getLinkedAssetFrameStatusAndDiff = (
  currentLinkedAssetFrame: LinkedAssetFrame,
  previousLinkedAssetFrame?: LinkedAssetFrame
): { status: LinkedAssetFrameStatus; updatedLinkedAssetFrame?: TLinkedAssetFramesTableEntity } => {
  if (!previousLinkedAssetFrame) {
    return { status: LinkedAssetFrameStatus.ADDED };
  }

  const updatedLinkedAssetFrame = getUpdatedLinkedAssetFrame(
    currentLinkedAssetFrame,
    previousLinkedAssetFrame
  );
  if (updatedLinkedAssetFrame) {
    return { status: LinkedAssetFrameStatus.UPDATED, updatedLinkedAssetFrame };
  }

  return { status: LinkedAssetFrameStatus.UNCHANGED };
};

export const getLinkedAssetFramesByStatus = (
  currentLinkedAssetFrames: LinkedAssetFrame[],
  previousLinkedAssetFrames: LinkedAssetFrame[]
) => {
  const linkedAssetFrames: Record<LinkedAssetFrameStatus, TLinkedAssetFramesTableEntity[]> = {
    [LinkedAssetFrameStatus.REMOVED]: [],
    [LinkedAssetFrameStatus.ADDED]: [],
    [LinkedAssetFrameStatus.UPDATED]: [],
    [LinkedAssetFrameStatus.UNCHANGED]: [],
  };

  const previousLinkedAssetFramesMap = new Map(
    previousLinkedAssetFrames.map((frame) => [frame.spaceId, frame])
  );

  currentLinkedAssetFrames.forEach((linkedAssetFrame: LinkedAssetFrame) => {
    const previousLinkedAssetFrame = previousLinkedAssetFramesMap.get(linkedAssetFrame.spaceId);
    const { status, updatedLinkedAssetFrame } = getLinkedAssetFrameStatusAndDiff(
      linkedAssetFrame,
      previousLinkedAssetFrame
    );

    linkedAssetFrames[status].push(
      updatedLinkedAssetFrame ?? linkedAssetFrameWithoutDiff(linkedAssetFrame, status)
    );
    previousLinkedAssetFramesMap.delete(linkedAssetFrame.spaceId);
  });

  previousLinkedAssetFramesMap.forEach((frame) => {
    linkedAssetFrames[LinkedAssetFrameStatus.REMOVED].push(
      linkedAssetFrameWithoutDiff(frame, LinkedAssetFrameStatus.REMOVED)
    );
  });
  return linkedAssetFrames;
};
